import {Breadcrumbs, Grid, Typography} from "@mui/material";
import {useEffect, useState} from "react";
import {useParams, usePathname} from "next/navigation";
import Link from "next/link";
import {useTranslation} from "react-i18next";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RoleName from "@/components/breadcrumb/names/roleName";
import UserName from "@/components/breadcrumb/names/userName";
import LocationName from "@/components/breadcrumb/names/locationName";
import CycleName from "@/components/breadcrumb/names/cycleName";
import TemplateName from "@/components/breadcrumb/names/templateName";
import StudentRegistrationName from "@/components/breadcrumb/names/studentRegistrationName";

interface Item {
    label: string;
    link?: string | null
}

const doNotAddLinkItems = ["dashboard", "schedules", "siteSettings", "finance"];
const doNotIncludeItems = ["self", "induction"];

const separator = {
    borderRadius: "3px",
    ".MuiBreadcrumbs-separator": {
        margin: "0 1px"
    }
}

const label = {
    fontSize: "0.9rem"
}

const constructBackwardLink = (temp:string[], x: number) => {
    let html = "";
    for(let i = 1; i <= x; i++) {
        html += `/${temp[i]}`
    }
    return html
}

const Breadcrumb = ({heights, sidePadding}: {
    heights: { header: number; breadcrumb: number };
    sidePadding: { paddingLeft: number; paddingRight: number } | null
}) => {
    const {t} = useTranslation("breadcrumb");
    const [items, setItems] = useState<Item[]>([]);
    const pathName = usePathname();
    const params = useParams();

    const handleNames = (val: string | number) => {
        const {roleId, userId, pid, cycleId, templateId, registrationId} = params;
        if(val === roleId) return <RoleName uid={Number(val)} />;
        if(val === userId) return <UserName uid={Number(val)} />;
        if(val === pid) return <LocationName uid={Number(val)} />;
        if(val === cycleId) return <CycleName uid={Number(val)} />;
        if(val === templateId) return <TemplateName uid={Number(val)} />;
        if(val === registrationId) return <StudentRegistrationName uid={Number(val)} />;
        return t(val as any)
    };

    useEffect(() => {
        const pathName = window.location.pathname;
        const tempItems: { label: string; link?: string | null }[] = [];
        if (pathName) {
            const temp = pathName.split('/');
            if (temp?.length > 1) {
                for (let i = 1; i < temp.length; i++) {
                    if (!doNotIncludeItems.includes(temp[i])) {
                        if (i === temp.length - 1) {
                            tempItems.push({
                                // @ts-ignore
                                label: handleNames(temp[i])
                            })
                        } else {
                            tempItems.push({
                                label: t(temp[i] as any),
                                link: !doNotAddLinkItems.includes(temp[i]) ? constructBackwardLink(temp, i) : null
                            })
                        }
                    }

                }
            }
        }
        setItems(tempItems)
    }, [pathName])

    return <Grid className="df aic pf tpl we1" container sx={{
        ...separator,
        ml: 1,
        background: theme => theme.palette?.backgrounds?.breadcrumb,
        height: `${heights.breadcrumb}rem`,
        top: `${heights.header}rem`,
        paddingLeft: `${sidePadding?.paddingLeft ? (sidePadding?.paddingLeft + 5) : 0}px`,
        paddingRight: `${sidePadding?.paddingRight ? (sidePadding?.paddingLeft + 5) : 0}px`,
        borderBottom: theme => `1px solid ${theme.palette?.borders?.mainDivider}`,
    }}>
        <Breadcrumbs separator={<NavigateNextIcon sx={{fontSize: "1.2rem"}}/>} aria-label="breadcrumb"
                     sx={{fontSize: "0.9rem"}}>
            {items.map((item: Item) => {
                return item.link ?
                    <Link style={{color: "#3899E5", ...label}} key={item.label} href={item.link}>{item.label}</Link> :
                    <Typography key={item.label}
                                sx={{color: 'text.primary', ...label, marginTop: "1px"}}>{item.label}</Typography>
            })}
        </Breadcrumbs>
    </Grid>
}

export default Breadcrumb;